import { Component, type ReactNode, type ErrorInfo } from "react";
import i18n from "i18next";
import { Sentry } from "@/lib/sentry";
import { RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SectionErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
  fallbackMessage?: string;
  retryLabel?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`[SectionErrorBoundary${this.props.name ? `: ${this.props.name}` : ""}]`, error, errorInfo);
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        sectionName: this.props.name,
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const message = this.props.fallbackMessage
        ?? i18n.t("errors.sectionLoadFailed", "This section couldn\u2019t load");
      const retry = this.props.retryLabel
        ?? i18n.t("errors.tryAgain", "Try again");

      return (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-8 text-center">
          <Alert variant="destructive">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
          <button
            onClick={this.handleRetry}
            className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {retry}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
